/* components.jsx — shared UI atoms, charts, and helpers
 * Design: v6 (Plus Jakarta Sans, new tokens, SVG charts, Toast)
 * Logic:  v5 (FilterBar, useFilteredRows, PhoneInput, EnumSelect, statusTone)
 */

import React from 'react'
import { I } from './icons.jsx'
import { humanize, formatPhone, ENUM_OPTIONS } from './utils.jsx'

/* ===== Pill ===== */
function Pill({ children, tone }) {
  return <span className={"pill " + (tone || "")}>{children}</span>;
}

function StatusDot({ tone }) {
  const map = { ok: "var(--ok)", warn: "var(--warn)", danger: "var(--danger)", info: "var(--info)", cyan: "var(--cyan)", pink: "var(--pink)", muted: "var(--text-faint)" };
  return <span style={{ width: 8, height: 8, borderRadius: 999, background: map[tone] || "var(--text-faint)", display: "inline-block" }} />;
}

/* ===== statusTone (v5 logic) ===== */
function statusTone(status) {
  switch (status) {
    case "delivered": case "paid": return "ok";
    case "preparing": case "out_for_delivery": return "warn";
    case "ready": return "cyan";
    case "cancelled": return "danger";
    default: return "info";
  }
}

/* ===== Drawer ===== */
function Drawer({ title, subtitle, onClose, children, footer, width }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="drawer-mask" onClick={(e) => { if (e.target.classList.contains("drawer-mask")) onClose(); }}>
      <div className="drawer" style={width ? { width } : undefined}>
        <div className="drawer-head">
          <div>
            <div className="title">{title}</div>
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">{I.x}</button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ===== Modal ===== */
function Modal({ title, onClose, children, footer }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-mask" onClick={(e) => { if (e.target.classList.contains("modal-mask")) onClose(); }}>
      <div className="modal">
        <div className="modal-head">
          <h4>{title}</h4>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">{I.x}</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ===== Field ===== */
function Field({ label, help, error, children, required }) {
  return (
    <label className="field">
      <span className="field-label">{label}{required && <span style={{ color: "var(--danger)" }}> *</span>}</span>
      {children}
      {help && <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 6 }}>{help}</div>}
      {error && <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 4 }}>{error}</div>}
    </label>
  );
}

/* ===== EnumSelect (v5) ===== */
function EnumSelect({ value, onChange, options, placeholder, allowEmpty }) {
  return (
    <select className="select" value={value || ""} onChange={(e) => onChange(e.target.value || null)}>
      {(allowEmpty || placeholder) && <option value="">{placeholder || "— Select —"}</option>}
      {(options || []).map((o) => (
        <option key={o} value={o}>{humanize(o)}</option>
      ))}
    </select>
  );
}

/* ===== PhoneInput (v5) ===== */
function PhoneInput({ value, onChange, error }) {
  const cc = (window.WYF_CONFIG && window.WYF_CONFIG.DEFAULT_COUNTRY_CODE) || "+63";
  const local = value ? value.replace(/^\+63/, "0").replace(new RegExp(`^\\${cc}`), "") : "";
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <div className="input" style={{ width: 56, flexShrink: 0, textAlign: "center", color: "var(--text-muted)", cursor: "default" }}>{cc}</div>
      <input
        className="input"
        type="tel"
        value={local}
        placeholder="09XXXXXXXXX"
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw ? formatPhone(raw, cc) : "");
        }}
        style={error ? { borderColor: "var(--danger)" } : undefined}
      />
    </div>
  );
}

/* ===== EmptyState ===== */
function EmptyState({ title, hint }) {
  return (
    <div className="empty">
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: "var(--text-muted)" }}>{title || "Nothing here"}</div>
      {hint && <div style={{ color: "var(--text-faint)", fontSize: 12 }}>{hint}</div>}
    </div>
  );
}

/* ===== Spinner ===== */
function Spinner({ label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "48px 20px", color: "var(--text-faint)" }}>
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="13" stroke="var(--border-strong)" strokeWidth="3"/>
        <path d="M16 3 A13 13 0 0 1 29 16" stroke="var(--cyan)" strokeWidth="3" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite"/>
        </path>
      </svg>
      {label && <div style={{ fontSize: 13 }}>{label}</div>}
    </div>
  );
}

/* ===== FilterBar (v5 logic, v6 look) ===== */
function FilterBar({ filters, values, onChange, children }) {
  const hasAny = filters.some(f => values[f.key]);
  return (
    <div className="filter-bar">
      {filters.map((f) => {
        if (f.type === "search") {
          return (
            <div key={f.key} className="searchbar" style={{ marginBottom: 0, flex: "0 1 280px", minWidth: 200 }}>
              <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
              <input
                placeholder={f.placeholder || "Search…"}
                value={values[f.key] || ""}
                onChange={(e) => onChange({ ...values, [f.key]: e.target.value })}
              />
            </div>
          );
        }
        if (f.type === "select") {
          return (
            <select key={f.key} className="select" style={{ width: "auto", minWidth: 160 }}
              value={values[f.key] || ""}
              onChange={(e) => onChange({ ...values, [f.key]: e.target.value })}>
              <option value="">{f.label || "All"}</option>
              {(f.options || []).map((o) => (
                <option key={o} value={o}>{humanize(o)}</option>
              ))}
            </select>
          );
        }
        if (f.type === "date") {
          return (
            <input key={f.key} type="date" className="input" style={{ width: "auto" }}
              value={values[f.key] || ""}
              placeholder={f.placeholder}
              onChange={(e) => onChange({ ...values, [f.key]: e.target.value })} />
          );
        }
        return null;
      })}
      {hasAny && (
        <button className="btn btn-ghost btn-sm"
          onClick={() => onChange(Object.fromEntries(filters.map(f => [f.key, ""])))}>
          Clear
        </button>
      )}
      {children && <><span className="spacer" style={{ flexShrink: 0 }}/>{children}</>}
    </div>
  );
}

/* ===== useFilteredRows (v5 logic) ===== */
function useFilteredRows(rows, filters, values, opts = {}) {
  return React.useMemo(() => {
    let out = rows;
    for (const f of filters) {
      const val = values[f.key];
      if (!val) continue;
      if (f.type === "search") {
        const q = val.toLowerCase();
        const fields = f.fields || ["name"];
        out = out.filter(r => fields.some(fld => (r[fld] || "").toLowerCase().includes(q)));
      } else if (f.type === "select") {
        const col = f.column || f.key;
        out = out.filter(r => r[col] === val);
      } else if (f.type === "date") {
        const dateCol = f.column || opts.dateColumn || "created_at";
        const rowDate = (r) => (r[dateCol] || "").slice(0, 10);
        if (f.key === "from" || f.key.endsWith("_from")) out = out.filter(r => rowDate(r) >= val);
        if (f.key === "to" || f.key.endsWith("_to"))   out = out.filter(r => rowDate(r) <= val);
      }
    }
    return out;
  }, [rows, filters, values, opts]);
}

/* ===== NavItem ===== */
function NavItem({ id, icon, label, badge, active, onClick }) {
  return (
    <button className={"nav-item" + (active ? " active" : "")} onClick={onClick}>
      <span className="nav-ico">{icon}</span>
      <span>{label}</span>
      {badge ? <span className="nav-badge">{badge}</span> : null}
    </button>
  );
}

/* ===== Toast system ===== */
const ToastCtx = React.createContext({ push: () => {} });
function useToast() { return React.useContext(ToastCtx); }

function ToastHost({ children }) {
  const [items, setItems] = React.useState([]);
  const push = React.useCallback((t) => {
    const id = Math.random().toString(36).slice(2);
    setItems((it) => [...it, { id, ...t }]);
    setTimeout(() => setItems((it) => it.filter(x => x.id !== id)), t.duration || 3200);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="toast-wrap">
        {items.map((t) => (
          <div key={t.id} className={"toast " + (t.tone || "ok")}>
            <div className="ico">{t.tone === "danger" ? I.warn : t.tone === "info" ? I.bell : I.check}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t.title}</div>
              {t.msg && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.msg}</div>}
            </div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ===== SVG Charts (v6 — no Chart.js dependency) ===== */

function Sparkline({ data, color = "var(--cyan)", height = 36 }) {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const w = 200, h = height;
  const stepX = w / Math.max(data.length - 1, 1);
  const pts = data.map((v, i) => `${i * stepX},${h - ((v - min) / (max - min || 1)) * (h - 4) - 2}`).join(" ");
  const areaPts = `0,${h} ${pts} ${w},${h}`;
  const gid = "sparkg-" + Math.random().toString(36).slice(2, 7);
  return (
    <svg className="kpi-sparkline" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${gid})`} points={areaPts} />
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

function GroupedBarChart({ data, keys, colors, suffix = "", height = 220 }) {
  const w = 600, h = height;
  const padL = 44, padR = 12, padT = 12, padB = 28;
  const max = Math.max(...data.flatMap(d => keys.map(k => d[k] || 0)), 1) * 1.1;
  const gw = (w - padL - padR) / Math.max(data.length, 1);
  const barW = (gw - 16) / keys.length;
  const ticks = 4;
  return (
    <svg className="bar-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const y = padT + ((h - padT - padB) * i) / ticks;
        const v = Math.round((max * (ticks - i)) / ticks);
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="rgba(255,255,255,0.05)" />
            <text x={padL - 8} y={y + 3} fontSize="9" fill="var(--text-faint)" textAnchor="end" fontFamily="var(--mono)">
              {v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => (
        <g key={i} transform={`translate(${padL + i * gw + 8},0)`}>
          {keys.map((k, j) => {
            const v = d[k] || 0;
            const bh = ((v / max) * (h - padT - padB));
            const x = j * (barW + 2);
            const y = h - padB - bh;
            return (
              <g key={k}>
                <rect x={x} y={y} width={barW} height={bh} rx="4" fill={colors[j]} opacity="0.95">
                  <title>{`${d.label} · ${k}: ${v}${suffix}`}</title>
                </rect>
              </g>
            );
          })}
          <text x={(gw - 16) / 2} y={h - 10} fontSize="10" fill="var(--text-faint)" textAnchor="middle">{d.label}</text>
        </g>
      ))}
    </svg>
  );
}

function SimpleBarChart({ data, color = "var(--cyan)", suffix = "%", height = 220 }) {
  const w = 600, h = height;
  const padL = 36, padR = 12, padT = 12, padB = 40;
  const max = Math.max(...data.map(d => d.value || 0), 1) * 1.15;
  const gw = (w - padL - padR) / Math.max(data.length, 1);
  const barW = gw - 10;
  const ticks = 4;
  return (
    <svg className="bar-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="bar-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4fc8e0" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const y = padT + ((h - padT - padB) * i) / ticks;
        const v = Math.round((max * (ticks - i)) / ticks);
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="rgba(255,255,255,0.05)" />
            <text x={padL - 6} y={y + 3} fontSize="9" fill="var(--text-faint)" textAnchor="end" fontFamily="var(--mono)">{v}{suffix}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const bh = ((d.value || 0) / max) * (h - padT - padB);
        const x = padL + i * gw + 5;
        const y = h - padB - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx="6" fill="url(#bar-grad)" />
            <text x={x + barW / 2} y={h - 22} fontSize="11" fill="var(--text)" textAnchor="middle" fontWeight="600">{d.label}</text>
            <text x={x + barW / 2} y={h - 8} fontSize="9" fill="var(--text-faint)" textAnchor="middle">{d.sub}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Donut({ data, height = 220 }) {
  const w = height, h = height;
  const cx = w / 2, cy = h / 2, r = h / 2 - 16, ir = r - 22;
  const total = data.reduce((s, d) => s + d.value, 0);
  let acc = 0;
  const TAU = Math.PI * 2;
  const arcs = data.map((d, idx) => {
    const a0 = (acc / total) * TAU - Math.PI / 2;
    acc += d.value;
    const a1 = (acc / total) * TAU - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const xi0 = cx + ir * Math.cos(a0), yi0 = cy + ir * Math.sin(a0);
    const xi1 = cx + ir * Math.cos(a1), yi1 = cy + ir * Math.sin(a1);
    return (
      <path key={idx} d={`M${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${ir} ${ir} 0 ${large} 0 ${xi0} ${yi0} Z`} fill={d.color} />
    );
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "8px 6px" }} className="donut-row">
      <svg className="donut-chart" viewBox={`0 0 ${w} ${h}`} width={h} height={h}>
        {arcs}
        <text x={cx} y={cy - 4} fontSize="22" fontWeight="800" fill="var(--text)" textAnchor="middle" fontFamily="var(--font)">{total}</text>
        <text x={cx} y={cy + 14} fontSize="10" fill="var(--text-faint)" textAnchor="middle" letterSpacing="2">ORDERS</text>
      </svg>
      <div className="legend" style={{ flexDirection: "column", gap: 10 }}>
        {data.map((d, i) => (
          <div className="item" key={i} style={{ justifyContent: "space-between", width: 170 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className="swatch" style={{ background: d.color }}></span>
              <span style={{ color: "var(--text)" }}>{d.label}</span>
            </span>
            <span className="mono" style={{ color: "var(--text-faint)", fontSize: 11 }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AreaLineChart({ data, height = 220 }) {
  const w = 600, h = height;
  const padL = 40, padR = 16, padT = 12, padB = 28;
  const max = Math.max(...data.map(d => d.value || 0), 1) * 1.15;
  const stepX = (w - padL - padR) / Math.max(data.length - 1, 1);
  const pts = data.map((d, i) => `${padL + i * stepX},${padT + (1 - (d.value || 0) / max) * (h - padT - padB)}`);
  const ticks = 4;
  return (
    <svg className="line-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4fc8e0" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const y = padT + ((h - padT - padB) * i) / ticks;
        const v = Math.round((max * (ticks - i)) / ticks);
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="rgba(255,255,255,0.05)" />
            <text x={padL - 6} y={y + 3} fontSize="9" fill="var(--text-faint)" textAnchor="end" fontFamily="var(--mono)">{(v / 1000).toFixed(0)}k</text>
          </g>
        );
      })}
      <polygon fill="url(#area-grad)" points={`${padL},${h - padB} ${pts.join(" ")} ${w - padR},${h - padB}`} />
      <polyline fill="none" stroke="url(#line-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={pts.join(" ")} />
      {data.map((d, i) => {
        const x = padL + i * stepX;
        const y = padT + (1 - (d.value || 0) / max) * (h - padT - padB);
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="3.5" fill="var(--bg)" stroke="url(#line-grad)" strokeWidth="2" />
            <text x={x} y={h - 10} fontSize="10" fill="var(--text-faint)" textAnchor="middle">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ===== ConfirmModal ===== */
function ConfirmModal({ title, message, confirmLabel = "Confirm", danger = false, onConfirm, onClose }) {
  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost" type="button" onClick={onClose}>Cancel</button>
        <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} type="button"
                onClick={() => { onClose(); onConfirm(); }}>
          {confirmLabel}
        </button>
      </>
    }>
      {message && <p style={{ margin: 0, fontSize: 14 }}>{message}</p>}
    </Modal>
  );
}

/* ===== ErrorBar (v5) — unchanged, renders below everything ===== */
// ErrorBar is already exported from errors.jsx; no redefinition needed here.

export {
  Pill, StatusDot, statusTone,
  Drawer, Modal, ConfirmModal, Field,
  EnumSelect, PhoneInput,
  EmptyState, Spinner,
  FilterBar, useFilteredRows,
  NavItem,
  ToastHost, useToast,
  Sparkline, GroupedBarChart, SimpleBarChart, Donut, AreaLineChart,
};
